/** User, team, session, invitation contracts — spec §6.1, §6.3, §6.20, §6.21. */
import { z } from 'zod';
import { DESIGNATIONS, NOTIFICATION_TYPES, SYSTEM_ROLES, USER_STATUSES, } from './enums.js';
import { hexColourSchema, objectIdSchema } from './common.js';
export const E164 = /^\+[1-9]\d{7,14}$/;
export const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const passwordSchema = z
    .string()
    .min(10, 'Password must be at least 10 characters')
    .max(200);
/**
 * Whether `Intl` will accept this as a timezone — V10 §4.
 *
 * The only definition of "valid" that matters, because `Intl` is what every
 * date calculation in this system ultimately calls, and it does not degrade on
 * a string it does not know: it throws `RangeError`. A free-text field let
 * `"Asia/Calcutta "` into a workspace document, and that one trailing space
 * ended the nightly attendance sweep every fifteen minutes (§1).
 *
 * Memoised because this runs on every profile save and on the invite path,
 * and constructing a formatter to ask a yes/no question is not free.
 */
const timezoneValidity = new Map();
/**
 * `Region/City`, with no whitespace anywhere — the shape of an IANA id.
 *
 * `Intl` alone is too generous to be the whole rule: it also accepts legacy
 * abbreviations like `"IST"`, which are ambiguous by construction (Indian,
 * Irish and Israeli standard time all claim it) and are not what §4 means by
 * "the stored value remains an IANA id". `UTC` is the one accepted exception,
 * because it is an identifier in its own right.
 */
const IANA_SHAPE = /^[A-Za-z][A-Za-z0-9+-]*(?:\/[A-Za-z0-9_+-]+)+$/;
export function isValidTimezone(value) {
    const cached = timezoneValidity.get(value);
    if (cached !== undefined)
        return cached;
    let valid = value === 'UTC' || IANA_SHAPE.test(value);
    if (valid) {
        try {
            new Intl.DateTimeFormat('en', { timeZone: value });
        }
        catch {
            valid = false;
        }
    }
    timezoneValidity.set(value, valid);
    return valid;
}
/**
 * A stored timezone.
 *
 * Validated rather than merely length-checked since V10 §4: the picker cannot
 * produce anything else, and the schema is what makes that true for every
 * other way in — an API client, an import, a script.
 */
export const timezoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine(isValidTimezone, 'Not a timezone this system recognises — pick one from the list');
export const notificationMatrixEntrySchema = z.object({
    inApp: z.boolean(),
    email: z.boolean(),
    whatsapp: z.boolean(),
    push: z.boolean(),
});
/**
 * The event × channel matrix.
 *
 * Keyed over plain strings with an explicit key check rather than
 * `z.record(z.enum(...), …)`: an enum-keyed record **silently drops** entries
 * whose value fails to parse, so a client sending a partial entry got a 200 and
 * an empty matrix back — a preference the user believes they saved and did not.
 * A bad entry must be an error, not a quiet deletion.
 */
export const notificationMatrixSchema = z
    .record(z.string(), notificationMatrixEntrySchema)
    .superRefine((value, ctx) => {
    for (const key of Object.keys(value)) {
        if (!NOTIFICATION_TYPES.includes(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [key],
                message: `Unknown notification type "${key}"`,
            });
        }
    }
});
export const quietHoursSchema = z.object({
    enabled: z.boolean(),
    start: z.string().regex(HHMM),
    end: z.string().regex(HHMM),
});
export const userPreferencesSchema = z.object({
    theme: z.enum(['light', 'dark', 'system']),
    density: z.enum(['comfortable', 'compact']),
    defaultView: z.string(),
    defaultProjectId: objectIdSchema.nullish(),
    quietHours: quietHoursSchema,
    notificationMatrix: notificationMatrixSchema,
});
export const updatePreferencesSchema = userPreferencesSchema.partial().strict();
export const updateUserSchema = z
    .object({
    name: z.string().min(1).max(120),
    avatarUrl: z.string().url().nullish(),
    phone: z.string().regex(E164, 'Phone must be E.164, e.g. +919812345678').nullish(),
    designation: z.enum(DESIGNATIONS),
    teamIds: z.array(objectIdSchema),
    skills: z.array(z.string().min(1).max(40)).max(30),
    timezone: timezoneSchema,
    locale: z.string().min(2).max(10),
    capacityHoursPerWeek: z.number().min(0).max(80),
    workingDays: z.array(z.number().int().min(0).max(6)).max(7),
    workingHours: z.object({ start: z.string().regex(HHMM), end: z.string().regex(HHMM) }),
})
    .strict()
    .partial();
/** systemRole is deliberately absent — role changes go through /users/:id/role. */
export const updateRoleSchema = z
    .object({ systemRole: z.enum(SYSTEM_ROLES) })
    .strict();
export const listUsersQuerySchema = z
    .object({
    q: z.string().max(120).optional(),
    designation: z.enum(DESIGNATIONS).optional(),
    teamId: objectIdSchema.optional(),
    status: z.enum(USER_STATUSES).optional(),
    role: z.enum(SYSTEM_ROLES).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
export const inviteUserSchema = z
    .object({
    email: z.string().email().toLowerCase(),
    systemRole: z.enum(SYSTEM_ROLES).refine((r) => r !== 'owner', {
        message: 'Cannot invite an owner — ownership moves via transfer only',
    }),
    designation: z.enum(DESIGNATIONS),
    teamIds: z.array(objectIdSchema).default([]),
    projectIds: z.array(objectIdSchema).default([]),
})
    .strict();
/**
 * Moving an address, on either side of the accepted line.
 *
 * The same body serves both because the difference is not in what the caller
 * sends but in what the server does with it: for a pending invitation the
 * record moves immediately, for a member it becomes a pending change that only
 * the person themselves can complete.
 */
export const changeEmailSchema = z
    .object({ email: z.string().email().toLowerCase() })
    .strict();
export const acceptInviteSchema = z
    .object({
    name: z.string().min(1).max(120),
    password: passwordSchema,
    timezone: timezoneSchema.default('Asia/Kolkata'),
})
    .strict();
export const loginSchema = z
    .object({
    email: z.string().email().toLowerCase(),
    password: z.string().min(1).max(200),
    totp: z.string().regex(/^\d{6}$/).optional(),
    recoveryCode: z.string().min(8).max(40).optional(),
})
    .strict();
export const forgotPasswordSchema = z
    .object({ email: z.string().email().toLowerCase() })
    .strict();
export const resetPasswordSchema = z
    .object({ token: z.string().min(10), password: passwordSchema })
    .strict();
export const changePasswordSchema = z
    .object({ currentPassword: z.string().min(1), newPassword: passwordSchema })
    .strict();
export const twoFactorVerifySchema = z.object({ totp: z.string().regex(/^\d{6}$/) }).strict();
export const twoFactorDisableSchema = z
    .object({ password: z.string().min(1), totp: z.string().regex(/^\d{6}$/) })
    .strict();
export const phoneVerifyStartSchema = z
    .object({ phone: z.string().regex(E164, 'Phone must be E.164, e.g. +919812345678') })
    .strict();
export const phoneVerifyConfirmSchema = z
    .object({ code: z.string().regex(/^\d{6}$/) })
    .strict();
export const deactivateUserSchema = z.object({}).strict().optional();
export const reassignWorkSchema = z
    .object({
    toUserId: objectIdSchema,
    includeStatuses: z.array(z.string()).optional(),
})
    .strict();
/* ── Teams ─────────────────────────────────────────────── */
export const createTeamSchema = z
    .object({
    name: z.string().min(1).max(80),
    description: z.string().max(500).optional(),
    colour: hexColourSchema.default('#2F6F4E'),
    leadId: objectIdSchema,
    memberIds: z.array(objectIdSchema).default([]),
})
    .strict();
export const updateTeamSchema = createTeamSchema.partial().strict();
export const teamMembersSchema = z.object({ userIds: z.array(objectIdSchema).min(1) }).strict();
/** V3 R5 — company sign-up. */
export const signupSchema = z
    .object({
    companyName: z.string().trim().min(2).max(120),
    slug: z
        .string()
        .trim()
        .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers and hyphens only')
        .min(2)
        .max(40)
        .optional(),
    name: z.string().trim().min(2).max(120),
    email: z.string().email().max(160),
    password: z.string().min(10).max(200),
})
    .strict();
//# sourceMappingURL=user.js.map